import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { ConditionName, IAddCommentProps, zIndex } from "interfaces";
import { conditions } from "helpers";
import IMAGES from "../../../../../images/importImages";
import { handleSubmitComment } from "redux-share/actions/comment.action";
import { setReplyToAuthor } from "redux-share/slices/commentsSlice";
import { getLoginZarebinUrl } from "../../../../../services";
import { shallowEqual } from "react-redux";
import dynamic from "next/dynamic";
import DefaultMessages from "./defaultMessages";

const CloseIcon = dynamic(() => import("../../icon-components/closeIcon"));
const EmojiPicker = dynamic(() => import("../emojiPicker/emojiPicker"));

const AddComment = ({ type, setShowReplyBox }: IAddCommentProps) => {
    const [inputValue, setInputValue] = useState<string>("");
    const [selectedEmoji, setSelectedEmoji] = useState<string>("");
    const [showEmojiPickerPopover, setShowEmojiPickerPopover] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { replyToAuthor } = useAppSelector(({ comments: { replyToAuthor, canAddComment } }) => ({ replyToAuthor, canAddComment }), shallowEqual);
    useAppSelector(({ user: { isLogin } }) => ({ isLogin }), shallowEqual);
    const { videoElementSizeInfo } = useAppSelector(({ videoData: { videoElementSizeInfo } }) => ({ videoElementSizeInfo }), shallowEqual);

    const inputValueRef = useRef<string>(inputValue);
    const selectedEmojiRef = useRef<string>(selectedEmoji);

    useEffect(() => {
        inputValueRef.current = inputValue;
    }, [inputValue]);

    useEffect(() => {
        selectedEmojiRef.current = selectedEmoji;
    }, [selectedEmoji]);

    useEffect(() => {
        if (selectedEmoji) {
            setInputValue(`${inputValue}${selectedEmoji}`);
            setSelectedEmoji("");
        }
    }, [selectedEmoji]);

    useEffect(() => {
        const handleSendCommentInputKeyPress = (event: any) => {
            if (event.key === "Enter" && event.target.id === "send-comment-input" && (!!inputValueRef.current || !!selectedEmoji))
                handleSubmitComment(inputValueRef.current, setInputValue, selectedEmojiRef.current, setSelectedEmoji, type, setShowReplyBox);
        };
        document.addEventListener("keypress", handleSendCommentInputKeyPress);
        return () => document.removeEventListener("keypress", handleSendCommentInputKeyPress);
    }, []);

    const emojiListIcon = (
        <Image
            alt="emoji-list-icon"
            src={
                conditions(ConditionName.isDarkMode)
                    ? showEmojiPickerPopover
                        ? IMAGES.emojiHappyWhite
                        : IMAGES.keyboardWhite
                    : showEmojiPickerPopover
                    ? IMAGES.emojiHappyBlack
                    : IMAGES.keyboardBlack
            }
            onClick={() => setShowEmojiPickerPopover(!showEmojiPickerPopover)}
            className="pl-cursor-pointer"
            width={24}
            height={24}
        />
    );
    const commentInput = (
        <input
            id="send-comment-input"
            className={`pl-text-gray-600 pl-pt-[2px] dark:pl-text-secondary pl-font-normal placeholder:pl-text-gray-600 placeholder:dark:pl-text-secondary pl-w-full pl-bg-transparent pl-outline-none pl-border-none ${
                conditions(ConditionName.isTouchScreen) ? "pl-text-[12px]" : "pl-text-[14px]"
            }`}
            placeholder={type === "reply" ? "پاسخ خود را وارد کنید" : "دیدگاه خود را وارد کنید"}
            onChange={(event: any) => setInputValue(event.target.value)}
            maxLength={200}
            value={inputValue}
        />
    );
    const sendCommentIcon = (
        <Image
            alt="send-comment"
            src={
                conditions(ConditionName.isTouchScreen)
                    ? IMAGES.sendCommentTouchScreen
                    : conditions(ConditionName.isDarkMode)
                    ? IMAGES.sendCommentDesktopWhite
                    : IMAGES.sendCommentDesktopBlack
            }
            onClick={() => handleSubmitComment(inputValue, setInputValue, selectedEmoji, setSelectedEmoji, type, setShowReplyBox)}
            className="pl-cursor-pointer"
            width={conditions(ConditionName.isTouchScreen) ? 40 : 24}
            height={conditions(ConditionName.isTouchScreen) ? 40 : 24}
        />
    );

    const addCommentContent = conditions(ConditionName.isTouchScreen) ? (
        <div className={`pl-absolute pl-top-[63px] pl-w-full pl-flex pl-flex-col pl-justify-end`}>
            <div className="pl-border-b pl-border-t-[2px] dark:pl-border-z-secondary-600 pl-bg-[#F6F8FF] dark:pl-bg-dark-3 pl-px-4 pl-py-3 pl-flex pl-flex-col pl-items-center pl-gap-4 pl-relative pl-z-10">
                {conditions(ConditionName.isLogin) ? (
                    <div className="pl-flex pl-w-full pl-gap-4 pl-items-center">
                        {conditions(ConditionName.canAddComment) ? (
                            <>
                                <div className="pl-flex pl-flex-1 pl-items-center pl-gap-1 pl-bg-gray-highLight dark:pl-bg-dark-1 pl-py-2 pl-px-3 pl-rounded-[20px]">
                                    {emojiListIcon}
                                    {commentInput}
                                    <div className="pl-text-[11px] pl-font-normal">
                                        <span>{inputValue.length}</span>
                                        <span>/</span>
                                        <span>200</span>
                                    </div>
                                </div>
                                {sendCommentIcon}
                            </>
                        ) : (
                            <p className="pl-flex pl-flex-1 pl-items-center pl-gap-1 pl-bg-gray-highLight dark:pl-bg-dark-1 pl-py-2 pl-px-3 pl-rounded-[20px] pl-text-[14px] pl-font-normal pl-text-secondary/[0.7]">
                                در حال حاضر امکان ثبت دیدگاه وجود ندارد.
                            </p>
                        )}
                    </div>
                ) : (
                    <p className="pl-w-full pl-flex pl-flex-1 pl-items-center pl-gap-1 pl-bg-gray-highLight dark:pl-bg-dark-1 pl-py-2 pl-px-3 pl-rounded-[20px] pl-text-secondary pl-text-[14px] pl-font-normal">
                        شما برای ارسال دیدگاه خود باید{" "}
                        {conditions(ConditionName.isZarebinSite) ? (
                            <a href={getLoginZarebinUrl()} className="text-primary-600 border-b cursor-pointer border-primary-600">
                                وارد
                            </a>
                        ) : (
                            "وارد"
                        )}{" "}
                        شوید.
                    </p>
                )}
                {conditions(ConditionName.hasCommentDefaultMessages) && type !== "reply" && (
                    <div className="pl-absolute pl-bottom-[50px] pl-w-full pl-h-[1px] pl-bg-gray-highLight dark:pl-bg-z-secondary-600" />
                )}
                {conditions(ConditionName.hasCommentDefaultMessages) && type !== "reply" && (
                    <div className="pl-flex pl-w-full pl-items-center pl-pt-2">
                        <DefaultMessages />
                    </div>
                )}
            </div>
            {type === "reply" && (
                <div
                    className="pl-flex pl-justify-between pl-items-center pl-text-[14px] pl-font-normal pl-bg-gray-highLight dark:pl-bg-z-gray-500 pl-text-secondary-500 pl-pr-8 pl-pl-4 pl-py-[9px] pl-relative"
                    style={{ zIndex: zIndex.commentAnswer }}
                >
                    <span> در حال پاسخ به {replyToAuthor.author ? replyToAuthor.author : "کاربر ناشناس"}</span>
                    <CloseIcon className="pl-w-6 pl-h-6 pl-stroke-gray-800 dark:pl-stroke-dark-5" onClick={() => dispatch(setReplyToAuthor({}))} />
                </div>
            )}
            {showEmojiPickerPopover && (
                <EmojiPicker
                    enableUnified={false}
                    setSelectedEmoji={setSelectedEmoji}
                    onClickOutside={() => setShowEmojiPickerPopover(false)}
                    parentClassName="-pl-bottom-[230px] pl-h-[300px]"
                />
            )}
        </div>
    ) : (
        <div className={`${type !== "reply" && "pl-border-b pl-border-secondary-300 dark:pl-border-gray-700"} pl-w-full`}>
            <div
                className={`pl-w-full ${
                    conditions(ConditionName.hasCommentDefaultMessages)
                        ? "pl-rounded-t-[20px] pl-bg-gray-100 dark:pl-bg-dark-2"
                        : "pl-rounded-[20px] pl-bg-gray-highLight dark:pl-bg-dark-2"
                } pl-py-3 pl-pr-4 pl-pl-6 pl-flex pl-items-center pl-gap-4 pl-relative ${
                    type === "reply" ? "pl-my-2" : conditions(ConditionName.hasCommentDefaultMessages) ? "pl-mt-6" : "pl-my-6"
                }`}
            >
                {conditions(ConditionName.isLogin) ? (
                    <>
                        {conditions(ConditionName.canAddComment) ? (
                            <>
                                {emojiListIcon}
                                {commentInput}
                                {sendCommentIcon}
                            </>
                        ) : (
                            <p className="pl-text-[14px] pl-font-normal pl-text-secondary/[0.7]">در حال حاضر امکان ثبت دیدگاه وجود ندارد.</p>
                        )}
                    </>
                ) : (
                    <p className="pl-text-secondary pl-text-[14px] pl-font-normal">
                        شما برای ارسال دیدگاه خود باید{" "}
                        {conditions(ConditionName.isZarebinSite) ? (
                            <a href={getLoginZarebinUrl()} className="pl-text-primary-600 pl-border-b pl-cursor-pointer pl-border-primary-600">
                                وارد
                            </a>
                        ) : (
                            "وارد"
                        )}{" "}
                        شوید.
                    </p>
                )}
                {type === "reply" && (
                    <button className="pl-text-[15px] pl-font-normal" onClick={() => setShowReplyBox && setShowReplyBox(false)}>
                        لغو
                    </button>
                )}
                {showEmojiPickerPopover && (
                    <EmojiPicker
                        enableUnified={false}
                        setSelectedEmoji={setSelectedEmoji}
                        onClickOutside={() => setShowEmojiPickerPopover(false)}
                        parentClassName="pl-bottom-[60px]"
                    />
                )}
            </div>
            {conditions(ConditionName.hasCommentDefaultMessages) && type !== "reply" && (
                <div
                    style={{
                        width: videoElementSizeInfo.width
                    }}
                    className={`pl-relative pl-flex pl-px-4 pl-rounded-b-[20px] pl-border-t pl-border-secondary-300 dark:pl-border-gray-700 pl-py-2 pl-mb-6 pl-bg-gray-100 dark:pl-bg-dark-2`}
                >
                    <DefaultMessages />
                </div>
            )}
        </div>
    );

    return addCommentContent;
};

export default AddComment;
